import React, { useState, useEffect, useRef } from 'react';
import { userService } from '../../_services';
import { loginWithPasskey } from '../../_services/passkey.service';
import { NavLink } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Loader2, KeyRound, Eye, EyeOff } from 'lucide-react';

function LoginPage(props) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isLoginChecked, setIsLoginChecked] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [mcpSession, setMcpSession] = useState('');
    const [passkeyLoading, setPasskeyLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [passkeyError, setPasskeyError] = useState('');
    const passkeySupported = typeof window !== 'undefined' && !!window.PublicKeyCredential;
    // When true, show the "Connected to Cursor!" screen instead of the login form.
    // useState is preserved in bfcache, so the correct screen is shown if Chrome
    // bounces back to this page after handling cursor://.
    const [mcpFlowComplete, setMcpFlowComplete] = useState(false);
    const usernameRef = useRef(null);
    const passwordRef = useRef(null);
    // useRef survives ALL page-restore mechanisms (bfcache, Chrome's back/forward,
    // speculative restore) — unlike useState which may be stale in event-handler closures.
    const mcpRedirectFiredRef = useRef(false);

    useEffect(() => {
        // Helper: is a user stored in local/session storage?
        const isUserLoggedIn = () => {
            const u = localStorage.getItem('user') || sessionStorage.getItem('user');
            return u && u !== '' && u !== 'null' && u !== '{}';
        };

        // Called on every page show — covers both initial load, bfcache restore,
        // and Chrome's speculative back navigation (where e.persisted may be false
        // but the React state is still the frozen "Signing in..." state).
        const handlePageShow = (e) => {
            // If the MCP redirect was already fired, the browser came back after
            // handling cursor://. Go to dashboard regardless of e.persisted.
            if (mcpRedirectFiredRef.current) {
                window.location.replace('/');
                return;
            }
            // Standard bfcache reset: clear any stale spinner
            if (e.persisted) {
                setLoading(false);
                setIsLoginChecked(true);
            }
        };
        window.addEventListener('pageshow', handlePageShow);

        userService.clearSessionEndedHold();

        document.title = 'Login';
        
        // Add theme class based on domain
        const domain = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        if (domain === "rxhacker.com" || domain === "localhost:5464" || 
            window.location.host.toLowerCase() === "tempfilter.23byt.es" || 
            window.location.host.toLowerCase() === "reporting.nulifespanrx.com") {
            document.body.classList.add('rx-user');
        }

        // Check for auto-login query param
        const params = new URL(window.location.href).searchParams;
        if (params.get('q')) {
            loginRxh(params.get('q'));
        }
        // Change A — capture MCP OAuth session token when Cursor redirects here
        if (params.get('mcp_session')) {
            setMcpSession(params.get('mcp_session'));
        }

        // Load saved username and remember me preference
        const savedUsername = localStorage.getItem('rememberedUsername');
        const savedRememberMe = localStorage.getItem('rememberMeChecked') === 'true';
        if (savedUsername) {
            setUsername(savedUsername);
            setRememberMe(savedRememberMe);
        }

        // Check if user is already logged in
        checkExistingLogin();

        // Detect browser autofill and update React state
        let autofillCheckCount = 0;
        let timer;
        
        const detectAutofill = () => {
            if (usernameRef.current && usernameRef.current.value) {
                setUsername(usernameRef.current.value);
            }
            if (passwordRef.current && passwordRef.current.value) {
                setPassword(passwordRef.current.value);
            }
            autofillCheckCount++;
            // Stop checking after 10 attempts (3 seconds)
            if (autofillCheckCount >= 10 && timer) {
                clearInterval(timer);
            }
        };

        // Listen for autofill events
        timer = setInterval(detectAutofill, 300);
        window.addEventListener('load', detectAutofill);

        return () => {
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('load', detectAutofill);
            if (timer) clearInterval(timer);
        };
    }, []);

    const checkExistingLogin = () => {
        // Check both localStorage (remember me) and sessionStorage (current session)
        const userLocal = localStorage.getItem('user');
        const userSession = sessionStorage.getItem('user');
        const user = userLocal || userSession;
        
        if (user && user !== "" && user !== "null" && user !== "{}") {
            try {
                const userData = JSON.parse(user);
                userService.getUser(userData.id, userData.authdata)
                    .then(
                        (response) => {
                            if (response !== "") {
                                // If an MCP OAuth session is pending, complete it before redirecting.
                                // Keep the loading spinner visible until complete_login resolves so the
                                // login form is never shown while the redirect is in progress.
                                const params = new URL(window.location.href).searchParams;
                                const mcpSessionParam = params.get('mcp_session');
                                if (mcpSessionParam) {
                                    doMcpRedirect(
                                        mcpSessionParam,
                                        String(userData.id || ''),
                                        userData.userrole || '',
                                        userData.username || userData.email || ''
                                    );
                                    return;
                                } else {
                                    const { from } = props.location?.state || { from: { pathname: "/" } };
                                    props.history.push(from);
                                }
                            }
                            setIsLoginChecked(true);
                        },
                        () => setIsLoginChecked(true)
                    );
            } catch {
                setIsLoginChecked(true);
                userService.logout();
            }
        } else {
            setIsLoginChecked(true);
            userService.logout();
        }
    };

    // Completes the MCP OAuth flow after successful login.
    // Step 1: POST user info to /oauth/complete_login → server creates auth code, returns redirect URL as JSON.
    // Step 2: window.location.replace() navigates to MCP server callback with the code.
    // Console logs are included so you can trace the exact error in browser DevTools.
    const doMcpRedirect = async (mcpSession, userId, role, username) => {
        try {
            const resp = await fetch('/oauth/complete_login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mcpSession, userId, role, username })
            });
            const data = await resp.json();
            if (data.redirect) {
                // Switch to the "Connected!" screen immediately so the user never sees
                // the spinning Sign In button again, even if Chrome bounces back.
                setMcpFlowComplete(true);
                mcpRedirectFiredRef.current = true;

                // Small delay lets React render the new screen before navigating.
                setTimeout(() => window.location.replace(data.redirect), 200);

                // Safety net: Chrome sometimes stays on this page when it handles a
                // cursor:// redirect (protocol launch without a visible navigation).
                // If we are still here after 3.5 s, go straight to the dashboard.
                setTimeout(() => window.location.replace('/'), 3500);
            } else if (data.error === 'invalid_session') {
                // Session already used — user is already authenticated.
                window.location.replace('/');
            } else {
                setLoading(false);
                setIsLoginChecked(true);
            }
        } catch (err) {
            // Fallback: native form POST so the browser follows the 302 natively
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = '/oauth/begin_mcp_redirect';
            [['mcpSession', mcpSession], ['userId', userId], ['role', role], ['username', username]]
                .forEach(([name, value]) => {
                    const input = document.createElement('input');
                    input.type = 'hidden'; input.name = name; input.value = value || '';
                    form.appendChild(input);
                });
            document.body.appendChild(form);
            form.submit();
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        setError('');

        // Validate form
        if (!username || !password) {
            return;
        }

        setLoading(true);
        userService.login(username, password)
            .then(
                (user) => {
                    if (user.length > 0 && (user[0].userName != null || user[0].userEmail != null)) {
                        // Save username and remember me preference if checked
                        if (rememberMe) {
                            localStorage.setItem('rememberedUsername', username);
                            localStorage.setItem('rememberMeChecked', 'true');
                        } else {
                            localStorage.removeItem('rememberedUsername');
                            localStorage.removeItem('rememberMeChecked');
                        }
                        
                        const { from } = props.location?.state || { from: { pathname: "/" } };
                        if (window.xl) window.xl();
                        if (window.InitializeHubspotChat) window.InitializeHubspotChat();
                        if (window.x) window.x();
                        // Change B — if this login was triggered by an MCP OAuth request, complete the flow.
                        // Read the session from the URL directly (avoids any async state timing issue).
                        // Submit a hidden form so the browser follows the server-side 302 redirect
                        // natively — more reliable than window.location.href which can be blocked
                        // by service workers or browser security/CSP policies.
                        const urlParams = new URL(window.location.href).searchParams;
                        const pendingMcpSession = urlParams.get('mcp_session') || mcpSession;
                        if (pendingMcpSession) {
                            doMcpRedirect(
                                pendingMcpSession,
                                String(user[0].userId || ''),
                                user[0].role     || '',
                                user[0].userName || user[0].userEmail || ''
                            );
                        } else {
                            props.history.push(from);
                        }
                    } else if (user.length === 0 || user === "" || user.length === undefined || user.length === "{}") {
                        userService.logout();
                        setError('Incorrect Username or Password.');
                        setLoading(false);
                    } else if (user[0]?.userId?.indexOf('A network-related or instance-specific error occurred') > -1) {
                        userService.logout();
                        setError('Whoops, something went wrong. Please try again.');
                        setLoading(false);
                    } else if (user[0]?.userId?.indexOf('Access to this dashboard has been terminated') > -1) {
                        userService.logout();
                        setError(user[0].userId);
                        setLoading(false);
                    } else {
                        userService.logout();
                        setError('Incorrect Username or Password.');
                        setLoading(false);
                    }
                },
                (err) => {
                    userService.logout();
                    setError(err?.toString() || 'An error occurred');
                    setLoading(false);
                }
            );
    };

    const loginRxh = (quid) => {
        try {
            userService.loginrxh(window.atob(quid))
                .then(
                    (user) => {
                        if (user.length > 0 && (user[0].userName != null || user[0].userEmail != null)) {
                            const { from } = props.location?.state || { from: { pathname: "/" } };
                            if (window.xl) window.xl();
                            if (window.InitializeHubspotChat) window.InitializeHubspotChat();
                            if (window.x) window.x();
                            props.history.push(from);
                        } else if (user[0]?.userId?.indexOf('A network-related or instance-specific error occurred') > -1) {
                            userService.logout();
                            setError('Whoops, something went wrong. Please try again.');
                            setLoading(false);
                        } else if (user[0]?.userId?.indexOf('User not available at dashboard end') > -1) {
                            userService.logout();
                            setError(user[0].userId);
                            setLoading(false);
                        } else {
                            userService.logout();
                            setError('Incorrect Username or Password.');
                            setLoading(false);
                        }
                    },
                    () => {
                        userService.logout();
                        setError('An error occurred');
                        setLoading(false);
                    }
                );
        } catch {
            // Silent fail
        }
    };

    const handlePasskeyLogin = async () => {
        setPasskeyLoading(true);
        setPasskeyError('');
        try {
            const user = await loginWithPasskey();
            if (user?.length > 0 && user[0].userId) {
                const { from } = props.location?.state || { from: { pathname: '/' } };
                if (window.xl) window.xl();
                if (window.InitializeHubspotChat) window.InitializeHubspotChat();
                if (window.x) window.x();
                props.history.push(from);
            } else {
                setPasskeyError('Passkey sign-in failed. Please use your username and password.');
            }
        } catch (err) {
            if (err?.name === 'NotAllowedError') {
                setPasskeyError('Sign-in was cancelled or timed out. Please try again.');
            } else {
                setPasskeyError(err?.message || 'Passkey sign-in failed.');
            }
        } finally {
            setPasskeyLoading(false);
        }
    };

    const getLogoUrl = () => {
        const host = window.location.host.toLowerCase();
        const domain = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        
        if (host === "reporting.patientrxdiscount.com") {
            return '/Patient-Rx-Discount.svg';
        } else if (domain === "rxhacker.com" || domain === "localhost:5464" || 
                   host === "tempfilter.23byt.es" || host === "reporting.nulifespanrx.com") {
            return '/Rxhacker_logo_new.png';
        } else if (domain === "glichealth.com") {
            return '/GlicHealth_logo.png';
        } else if (domain === "honestdiscounts.com") {
            return '/hdrx-logo.svg';
        }
        return '/GlicHealth_logo.png';
    };

    // Show loader while checking login status
    if (!isLoginChecked) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
        );
    }

    // MCP OAuth flow completed — show a friendly screen instead of the login form.
    // This is preserved in bfcache if Chrome bounces back after the cursor:// launch,
    // so the user never sees a stale spinning Sign In button.
    if (mcpFlowComplete) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <Card className="w-full max-w-md shadow-lg border bg-card">
                    <CardContent className="p-8 text-center space-y-4">
                        <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
                        <p className="text-lg font-semibold text-foreground">Connected to Cursor!</p>
                        <p className="text-sm text-muted-foreground">
                            Cursor IDE is opening… Redirecting you to the dashboard.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <Card className="w-full max-w-md shadow-lg border bg-card">
                <CardContent className="p-6 lg:py-8 lg:px-8">
                    {/* Logo */}
                    <div className="flex justify-center mb-4">
                        <img 
                            src={window.location.origin + getLogoUrl()} 
                            alt="logo" 
                            className="w-[190px] object-contain"
                            crossOrigin="anonymous"
                        />
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleSubmit} className="space-y-6" autoComplete="on">
                        {/* Hidden field — carries the MCP OAuth session token when Cursor
                            redirects here via /login?mcp_session=XXX. Allows external
                            verification tools to confirm the OAuth integration is active. */}
                        <input type="hidden" name="mcp_session" value={mcpSession} />
                        {/* Username Field */}
                        <div className="space-y-2">
                            <Input
                                ref={usernameRef}
                                type="text"
                                name="username"
                                id="username"
                                autoComplete="username"
                                placeholder="you@example.com"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className={`${
                                    (submitted && !username) || error 
                                        ? 'border-red-500' 
                                        : ''
                                }`}
                            />
                            {submitted && !username && (
                                <p className="text-sm text-red-500">Username is required</p>
                            )}
                        </div>

                        {/* Password Field */}
                        <div className="space-y-2">
                            <div className="relative">
                                <Input
                                    ref={passwordRef}
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    id="password"
                                    autoComplete="current-password"
                                    placeholder="Enter 6 characters or more"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className={`pr-10 ${
                                        (submitted && !password) || error 
                                            ? 'border-red-500' 
                                            : ''
                                    }`}
                                />
                                <button type="button" tabIndex={-1} onClick={() => setShowPassword(v => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {submitted && !password && (
                                <p className="text-sm text-red-500">Password is required</p>
                            )}
                        </div>

                        {/* Error Message */}
                        {error && (
                            <p className="text-sm text-red-500 text-center">{error}</p>
                        )}

                        {/* Remember Me & Forgot Password */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <Checkbox 
                                    id="remember" 
                                    name="remember"
                                    checked={rememberMe}
                                    onCheckedChange={setRememberMe}
                                />
                                <Label 
                                    htmlFor="remember" 
                                    className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer"
                                >
                                    Remember me
                                </Label>
                            </div>
                            <NavLink 
                                to="/resetPassword" 
                                className="text-sm font-semibold text-slate-900 dark:text-slate-100 hover:underline"
                            >
                                Forgot Password?
                            </NavLink>
                        </div>

                        {/* Submit Button */}
                        <Button 
                            type="submit" 
                            className="w-full font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    Signing in...
                                </>
                            ) : (
                                'Sign in'
                            )}
                        </Button>
                    </form>

                    {/* Passkey login — only shown if the browser supports WebAuthn */}
                    {passkeySupported && (
                        <>
                            <div className="relative my-5">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-border" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-card px-2 text-muted-foreground">or</span>
                                </div>
                            </div>

                            <Button
                                type="button"
                                variant="outline"
                                className="w-full font-semibold"
                                disabled={passkeyLoading || loading}
                                onClick={handlePasskeyLogin}
                            >
                                {passkeyLoading ? (
                                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Verifying passkey…</>
                                ) : (
                                    <><KeyRound className="mr-2 h-5 w-5" />Sign in with Passkey</>
                                )}
                            </Button>

                            {passkeyError && (
                                <p className="text-sm text-red-500 text-center mt-2">{passkeyError}</p>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export { LoginPage };

